/**
 * useReminderSync — Foreground sync between Dexie reminders ↔ native notifications.
 *
 * Watches db.reminder + db.totodo for changes and:
 * 1. Denormalizes task data (title, body, link) into reminder records
 * 2. Cleans up expired / orphaned reminders
 * 3. Reconciles native local notifications via NativeBridge (schedule/cancel/reschedule)
 *
 * Must be mounted in AppLayout (Tier 2) — runs only when app is in foreground.
 *
 * Ported from v1: totodo-isolated-example/src/hooks/useReminderSync.ts
 */

import { useLiveQueryImmediate } from "@/db/hooks"
import { type MutableRefObject, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { Capacitor } from "@capacitor/core"
import { db } from "@/db/schema"
import { ObjectStatus, ObjectType, partitionDuplicateReminders, toMsTimestamp, parseDbDate } from "@totodo/operations"
import type { DbObject, DbReminder } from "@totodo/operations"
import { NativeBridge } from "@/shared/plugins/NativeBridge"
import { getTitleWithFallback } from "@/shared/utils/general/titleUtils"
import { htmlToPlainText } from "@/services/doc-processing"
import { addBreadcrumb } from "@/shared/telemetry/breadcrumbs"
import { logError } from "@/shared/telemetry/errorStorage"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMPARE_TITLE_CHARS = 80
const COMPARE_BODY_CHARS = 200
/** Reminders older than 1 hour are cleaned up. */
const CLEANUP_THRESHOLD_MS = 60 * 60 * 1000
/** Initial debounce to coalesce Dexie Cloud sync bursts. */
const DEBOUNCE_MS = 250
/** Follow-up debounce when changes arrive during sync. */
const FOLLOWUP_MS = 350
/** Maximum sync retries to prevent infinite loops. */
const MAX_RETRIES = 3

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ReminderSignature = Pick<
  DbReminder,
  "id" | "title" | "body" | "fireAt" | "link" | "kind" | "parentPath"
>

interface SnapshotEntry {
  reminder: DbReminder
  task?: (DbObject & { id: string }) | undefined
  parent?: (DbObject & { id: string }) | undefined
  parentId?: string
}

type Snapshot = SnapshotEntry[]

// ---------------------------------------------------------------------------
// Helpers — parentPath → parentId
// ---------------------------------------------------------------------------

function extractParentId(parentPath: string | undefined): string | undefined {
  if (!parentPath) return undefined
  const segments = parentPath.split("/")
  if (segments.length < 2) return undefined
  const id = segments[1]
  return id && !id.startsWith("#") ? id : undefined
}

// ---------------------------------------------------------------------------
// Helpers — deep link
// ---------------------------------------------------------------------------

function buildTaskLink(
  parentPath: string | undefined,
  taskId: string | undefined,
  parentType?: ObjectType,
): string {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://totodo.app"
  if (!taskId) return `${origin}/today`
  const tid = `?tid=${taskId}`

  if (!parentPath) return `${origin}/inbox${tid}`

  // Virtual containers
  if (parentPath === "root/#inbox") return `${origin}/inbox${tid}`
  if (parentPath.startsWith("root/#anyday")) return `${origin}/anyday${tid}`

  const parentId = extractParentId(parentPath)
  if (!parentId) return `${origin}/today${tid}`

  if (parentType === ObjectType.List) return `${origin}/list/${parentId}${tid}`
  if (parentType === ObjectType.Area) return `${origin}/area/${parentId}${tid}`
  return `${origin}/project/${parentId}${tid}`
}

// ---------------------------------------------------------------------------
// Helpers — display field denormalization
// ---------------------------------------------------------------------------

function truncateText(text: string, maxLen: number): string {
  return text.length <= maxLen ? text : text.slice(0, maxLen)
}

interface DisplayFields {
  title: string
  body: string
  link: string
}

function resolveDisplayFields(
  entry: SnapshotEntry,
  t: TFunction,
): DisplayFields | null {
  const task = entry.task
  if (!task?.id) return null

  const title = getTitleWithFallback({ title: task.title, t, objectType: task.type })
  const body = truncateText(htmlToPlainText(task.docHtml || ""), COMPARE_BODY_CHARS)
  const link = buildTaskLink(task.parentPath, task.id, entry.parent?.type)

  return { title, body, link }
}

// ---------------------------------------------------------------------------
// Helpers — signature comparison (avoids lodash.isEqual)
// ---------------------------------------------------------------------------

function sliceForCompare(value: string | undefined | null, maxChars: number): string {
  if (!value) return ""
  return value.length <= maxChars ? value : value.slice(0, maxChars)
}

function getFullSignature(r: DbReminder): ReminderSignature {
  return {
    id: r.id,
    title: r.title,
    body: r.body,
    fireAt: r.fireAt,
    link: r.link,
    kind: r.kind,
    parentPath: r.parentPath,
  }
}

function getCompareSignature(r: DbReminder): ReminderSignature {
  return {
    id: r.id,
    title: sliceForCompare(r.title, COMPARE_TITLE_CHARS),
    body: sliceForCompare(r.body, COMPARE_BODY_CHARS),
    fireAt: r.fireAt,
    link: r.link,
    kind: r.kind,
    parentPath: r.parentPath,
  }
}

function signaturesEqual(a: ReminderSignature, b: ReminderSignature): boolean {
  return (
    a.id === b.id &&
    a.fireAt === b.fireAt &&
    a.kind === b.kind &&
    a.link === b.link &&
    a.parentPath === b.parentPath &&
    a.title === b.title &&
    a.body === b.body
  )
}

function signatureArraysEqual(a: ReminderSignature[], b: ReminderSignature[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (!signaturesEqual(a[i]!, b[i]!)) return false
  }
  return true
}

function compareSignatureById(a: ReminderSignature, b: ReminderSignature): number {
  const aid = a.id ?? ""
  const bid = b.id ?? ""
  if (aid !== bid) return aid < bid ? -1 : 1

  const af = parseDbDate(a.fireAt) ?? NaN
  const bf = parseDbDate(b.fireAt) ?? NaN
  if (af !== bf) {
    if (!Number.isFinite(af)) return 1
    if (!Number.isFinite(bf)) return -1
    return af - bf
  }

  const ak = a.kind ?? ""
  const bk = b.kind ?? ""
  if (ak !== bk) return ak < bk ? -1 : 1
  return 0
}

// ---------------------------------------------------------------------------
// Helpers — validation
// ---------------------------------------------------------------------------

function isValidFutureReminder(r: DbReminder, nowMs: number): boolean {
  if (!r.id || !r.fireAt) return false
  const ms = parseDbDate(r.fireAt)
  return ms !== undefined && ms > nowMs + 1000
}

function shouldDisable(entry: SnapshotEntry): boolean {
  const task = entry.task
  if (!task?.id) return true
  if (task.isDeleted === 1) return true
  if (task.status === ObjectStatus.Completed || task.status === ObjectStatus.Canceled)
    return true
  // Recurring instance logically completed but status not yet synced
  if (task.instanceOutcome === "completed" || task.instanceOutcome === "skipped")
    return true
  return false
}

// ---------------------------------------------------------------------------
// reconcileNativeLocalNotifications — diff-based schedule/cancel
// ---------------------------------------------------------------------------

async function reconcileNative(
  currentReminders: DbReminder[],
  previous: {
    cancelKeys: ReminderSignature[]
    compareKeys: ReminderSignature[]
  },
): Promise<void> {
  const nowMs = Date.now()

  // Dedup duplicate reminders per task (legacy random-UUID rows coexisting with
  // a deterministic rem_<taskId>). Shared with the server's /v1/reminders/pending
  // so foreground and native background scheduling agree. Multi-offset reminders
  // (rem_<id>, rem_<id>_1, …) are preserved.
  const { kept: dedupedReminders } = partitionDuplicateReminders(currentReminders)

  const currentPresent = dedupedReminders.filter(
    (r) => r.id && parseDbDate(r.fireAt) !== undefined,
  )
  const scheduleEligible = new Set(
    currentPresent.filter((r) => isValidFutureReminder(r, nowMs)).map((r) => r.id!),
  )

  const currentCancelById = new Map(currentPresent.map((r) => [r.id!, getFullSignature(r)]))
  const currentCompareById = new Map(currentPresent.map((r) => [r.id!, getCompareSignature(r)]))
  const currentFullById = new Map(currentPresent.map((r) => [r.id!, r]))

  const prevCancelById = new Map(previous.cancelKeys.map((s) => [s.id!, s]))
  const prevCompareById = new Map(previous.compareKeys.map((s) => [s.id!, s]))

  const toCancel: ReminderSignature[] = []
  const toSchedule: DbReminder[] = []
  const toReschedule: Array<{ prev: ReminderSignature; next: DbReminder }> = []

  // Check removed / changed
  prevCancelById.forEach((prevCancel, id) => {
    const nextCompare = currentCompareById.get(id)
    if (!nextCompare) {
      toCancel.push(prevCancel)
      return
    }
    const prevCompare = prevCompareById.get(id)
    if (!prevCompare || !signaturesEqual(prevCompare, nextCompare)) {
      const nextFull = currentFullById.get(id)
      if (nextFull && scheduleEligible.has(id)) {
        toReschedule.push({ prev: prevCancel, next: nextFull })
      } else {
        toCancel.push(prevCancel)
      }
    }
  })

  // Check newly added
  currentCancelById.forEach((_cancel, id) => {
    if (!prevCancelById.has(id)) {
      const nextFull = currentFullById.get(id)
      if (nextFull && scheduleEligible.has(id)) toSchedule.push(nextFull)
    }
  })

  // Execute all ops
  const ops: Promise<unknown>[] = []

  for (const s of toCancel) {
    const cancelFireAtMs = parseDbDate(s.fireAt)
    if (cancelFireAtMs === undefined) continue
    ops.push(
      NativeBridge.cancelLocalNotification({
        reminderId: s.id!,
        kind: s.kind ?? "",
        title: s.title ?? "",
        body: s.body ?? "",
        link: s.link ?? "",
        fireAtMs: cancelFireAtMs,
        parentPath: s.parentPath ?? "",
      }),
    )
  }

  for (const { prev, next } of toReschedule) {
    const fireAtMs = parseDbDate(next.fireAt)
    if (fireAtMs === undefined) continue
    ops.push(
      NativeBridge.cancelLocalNotification({
        reminderId: prev.id!,
        kind: prev.kind ?? "",
        title: prev.title ?? "",
        body: prev.body ?? "",
        link: prev.link ?? "",
        fireAtMs: parseDbDate(prev.fireAt) ?? 0,
        parentPath: prev.parentPath ?? "",
      }),
    )
    ops.push(
      NativeBridge.scheduleLocalNotification({
        reminderId: next.id!,
        taskId: next.taskId,
        parentPath: next.parentPath ?? "",
        kind: next.kind ?? "",
        title: next.title ?? "",
        body: next.body ?? "",
        link: next.link ?? "",
        fireAtMs,
      }),
    )
  }

  for (const r of toSchedule) {
    const fireAtMs = parseDbDate(r.fireAt)
    if (fireAtMs === undefined) continue
    ops.push(
      NativeBridge.scheduleLocalNotification({
        reminderId: r.id!,
        taskId: r.taskId,
        parentPath: r.parentPath ?? "",
        kind: r.kind ?? "",
        title: r.title ?? "",
        body: r.body ?? "",
        link: r.link ?? "",
        fireAtMs,
      }),
    )
  }

  await Promise.allSettled(ops)

  if (toCancel.length > 0 || toSchedule.length > 0 || toReschedule.length > 0) {
    addBreadcrumb({
      category: "lifecycle",
      message: `[ReminderSync] reconcile: +${toSchedule.length} ~${toReschedule.length} -${toCancel.length}`,
      data: { scheduled: toSchedule.length, rescheduled: toReschedule.length, cancelled: toCancel.length },
    })
  }
}

// ---------------------------------------------------------------------------
// useReminderSync — main hook
// ---------------------------------------------------------------------------

export function useReminderSync(): void {
  const { t } = useTranslation()
  const isSyncingRef = useRef(false)
  const pendingSyncRef = useRef(false)
  const retryCountRef = useRef(0)
  const currentSnapshotRef = useRef<Snapshot | undefined>(undefined)
  const lastScheduledRef = useRef<{
    cancelKeys: ReminderSignature[]
    compareKeys: ReminderSignature[]
  }>({ cancelKeys: [], compareKeys: [] })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const followupRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Live query: reminders + tasks + parents (triple-reference) ──
  const [snapshot] = useLiveQueryImmediate<Snapshot>(async () => {
    // A reminder written with an ISO fireAt (add_reminder over MCP did, before 2026-10-06) is read as
    // the same instant in ms, so it still fires; new writes are always ms.
    const reminders = (await db.reminder.where("isDeleted").equals(0).toArray()).map((r) =>
      r.fireAt && !Number.isFinite(Number(r.fireAt)) ? { ...r, fireAt: toMsTimestamp(r.fireAt) ?? r.fireAt } : r,
    )
    if (reminders.length === 0) return []

    // Unique task IDs
    const taskIds = [
      ...new Set(
        reminders
          .map((r) => r.taskId)
          .filter((id): id is string => typeof id === "string" && id.length > 0),
      ),
    ]
    if (taskIds.length === 0) {
      return reminders.map((r) => ({
        reminder: r,
        task: undefined,
        parent: undefined,
        parentId: undefined,
      }))
    }

    // Fetch tasks
    const tasks = await db.totodo.where("id").anyOf(taskIds).toArray()
    const taskEntries = tasks.filter(
      (obj): obj is DbObject & { id: string } =>
        typeof obj.id === "string" && obj.id.length > 0,
    )
    const taskMap = new Map(taskEntries.map((t) => [t.id, t]))

    // Resolve parent IDs
    const parentIdByTaskId = new Map<string, string | undefined>()
    const parentIdsSet = new Set<string>()
    for (const task of taskEntries) {
      const pid = extractParentId(task.parentPath)
      parentIdByTaskId.set(task.id, pid)
      if (pid) parentIdsSet.add(pid)
    }

    // Fetch parents
    const parentIds = [...parentIdsSet]
    const parents =
      parentIds.length > 0
        ? await db.totodo.where("id").anyOf(parentIds).toArray()
        : []
    const parentMap = new Map(
      parents
        .filter((p): p is DbObject & { id: string } => typeof p.id === "string")
        .map((p) => [p.id, p]),
    )

    return reminders.map((r) => {
      const task = r.taskId ? taskMap.get(r.taskId) : undefined
      const parentId = task?.id ? parentIdByTaskId.get(task.id) : undefined
      const parent = parentId ? parentMap.get(parentId) : undefined
      return { reminder: r, task, parent, parentId }
    })
  }, ["reminderSync-snapshot"], [])

  // ── Effect: denormalize + cleanup + native reconcile ──
  useEffect(() => {
    currentSnapshotRef.current = snapshot
    if (!snapshot) return

    const syncAll = async (
      snap: Snapshot,
      snapshotRef: MutableRefObject<Snapshot | undefined>,
    ) => {
      isSyncingRef.current = true
      pendingSyncRef.current = false
      retryCountRef.current = 0

      try {
        const now = Date.now()
        const cleanupThreshold = now - CLEANUP_THRESHOLD_MS

        // ── 1. Determine denormalization updates + cleanups ──
        const updates: Array<{ id: string; title: string; body: string; link: string }> = []
        const toDelete = new Set<string>()

        for (const entry of snap) {
          const { reminder } = entry
          if (!reminder.id) continue

          // Auto-disable if task is deleted/completed/canceled
          if (shouldDisable(entry)) {
            toDelete.add(reminder.id)
            continue
          }

          if (!entry.task?.id) continue

          const fields = resolveDisplayFields(entry, t)
          if (!fields) continue

          // Compare truncated (what the notification UI shows)
          const existingTitle = sliceForCompare(reminder.title, COMPARE_TITLE_CHARS)
          const existingBody = sliceForCompare(reminder.body, COMPARE_BODY_CHARS)
          const nextTitle = sliceForCompare(fields.title, COMPARE_TITLE_CHARS)
          const nextBody = sliceForCompare(fields.body, COMPARE_BODY_CHARS)

          if (
            existingTitle !== nextTitle ||
            existingBody !== nextBody ||
            reminder.link !== fields.link
          ) {
            updates.push({
              id: reminder.id,
              title: fields.title,
              body: fields.body,
              link: fields.link,
            })
          }
        }

        // Expired reminders
        for (const { reminder } of snap) {
          if (
            typeof reminder.id === "string" &&
            (parseDbDate(reminder.fireAt) ?? Infinity) < cleanupThreshold
          ) {
            toDelete.add(reminder.id)
          }
        }

        // Stray duplicate reminders (legacy random-UUID rows coexisting with a
        // deterministic rem_<taskId>). Soft-deleting them here syncs the cleanup
        // to the server + all devices so the native scheduler stops double-firing.
        // Runs continuously — migration 004 only cleaned up once.
        const { dropped } = partitionDuplicateReminders(
          snap.map(({ reminder }) => reminder),
        )
        for (const r of dropped) {
          if (r.id) toDelete.add(r.id)
        }

        // ── 2. Batch write (single Dexie transaction) ──
        if (updates.length > 0 || toDelete.size > 0) {
          await db.transaction("rw", db.reminder, async () => {
            if (updates.length > 0) {
              await db.reminder.bulkUpdate(
                updates.map((u) => ({
                  key: u.id,
                  changes: { title: u.title, body: u.body, link: u.link },
                })),
              )
            }
            if (toDelete.size > 0) {
              const ts = new Date().toISOString()
              await db.reminder
                .where("id")
                .anyOf([...toDelete])
                .modify({ isDeleted: 1, deletedAt: ts })
            }
          })
        }

        // ── 3. Native reconciliation (iOS / Android only) ──
        if (Capacitor.isNativePlatform()) {
          const updateMap = new Map(updates.map((u) => [u.id, u]))

          const activeReminders = snap
            .filter(({ reminder }) => !toDelete.has(reminder.id ?? ""))
            .map(({ reminder }) => {
              if (!reminder.id) return undefined
              const u = updateMap.get(reminder.id)
              return u
                ? { ...reminder, title: u.title, body: u.body, link: u.link }
                : reminder
            })
            .filter((r): r is DbReminder => Boolean(r?.id))

          const nowMs = Date.now()
          const futureReminders = activeReminders.filter((r) =>
            isValidFutureReminder(r, nowMs),
          )

          const currentCompare = futureReminders
            .map(getCompareSignature)
            .sort(compareSignatureById)
          const currentCancel = futureReminders
            .map(getFullSignature)
            .sort(compareSignatureById)

          if (!signatureArraysEqual(currentCompare, lastScheduledRef.current.compareKeys)) {
            try {
              await reconcileNative(activeReminders, {
                cancelKeys: lastScheduledRef.current.cancelKeys,
                compareKeys: lastScheduledRef.current.compareKeys,
              })
              lastScheduledRef.current = {
                cancelKeys: currentCancel,
                compareKeys: currentCompare,
              }
            } catch (e) {
              console.warn("[ReminderSync] Native reconcile failed:", e)
              logError(e instanceof Error ? e : new Error(String(e)), {
                source: "manual",
                severity: "warning",
                category: "lifecycle",
                data: { hook: "useReminderSync", phase: "native_reconcile" },
              })
            }
          }
        }
      } catch (error) {
        console.warn("[ReminderSync] Sync failed:", error)
        logError(error instanceof Error ? error : new Error(String(error)), {
          source: "manual",
          severity: "warning",
          category: "lifecycle",
          data: { hook: "useReminderSync", phase: "sync" },
        })
      } finally {
        isSyncingRef.current = false

        // Follow-up if changes arrived during sync
        if (pendingSyncRef.current && retryCountRef.current < MAX_RETRIES) {
          pendingSyncRef.current = false
          retryCountRef.current++

          if (followupRef.current) clearTimeout(followupRef.current)
          followupRef.current = setTimeout(() => {
            if (isSyncingRef.current) {
              pendingSyncRef.current = true
              return
            }
            const current = snapshotRef.current
            if (current) void syncAll(current, snapshotRef)
          }, FOLLOWUP_MS)
        } else if (pendingSyncRef.current) {
          retryCountRef.current = 0
          pendingSyncRef.current = false
        }
      }
    }

    // ── Debounce: coalesce rapid Dexie Cloud sync bursts ──
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      if (isSyncingRef.current) {
        pendingSyncRef.current = true
        return
      }
      pendingSyncRef.current = false
      retryCountRef.current = 0

      const current = currentSnapshotRef.current
      if (current) void syncAll(current, currentSnapshotRef)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      if (followupRef.current) {
        clearTimeout(followupRef.current)
        followupRef.current = null
      }
    }
  }, [snapshot, t])
}
